import React from 'react';
import { Link } from 'react-router-dom';

export interface ServicesHeroStat {
  value: string;
  label: string;
}

export interface ServicesHeroData {
  badge: string;
  titlePrefix: string;
  titleHighlight: string;
  subtitle: string;
  ctaBook: string;
  ctaTeam: string;
  stats: ServicesHeroStat[];
}

interface ServicesHeroProps {
  data: ServicesHeroData;
  isEditing?: boolean;
  onChange?: (data: ServicesHeroData) => void;
}

const ServicesHero: React.FC<ServicesHeroProps> = ({ data, isEditing = false, onChange }) => {
  const handleStatChange = (index: number, field: keyof ServicesHeroStat, value: string) => {
    if (!onChange) return;
    const updated = data.stats.map((s, i) => (i === index ? { ...s, [field]: value } : s));
    onChange({ ...data, stats: updated });
  };

  return (
    <section className="relative min-h-[65vh] flex items-center justify-center overflow-hidden bg-[#050008] pt-20">
      {/* Background layers */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#0d0020] via-[#050008] to-[#000d1a]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_25%_50%,rgba(0,194,255,0.12)_0%,transparent_60%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_75%_50%,rgba(255,45,170,0.10)_0%,transparent_60%)]" />
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage: 'linear-gradient(rgba(0,194,255,0.8) 1px, transparent 1px), linear-gradient(90deg, rgba(0,194,255,0.8) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }}
      />

      <div className="relative z-10 max-w-5xl mx-auto px-4 md:px-8 text-center py-20 w-full">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 bg-white/5 border border-[#00c2ff]/30 rounded-full px-4 py-2 mb-7">
          <i className="ri-service-line text-[#00c2ff] text-sm" />
          {isEditing ? (
            <input
              type="text"
              value={data.badge}
              onChange={(e) => onChange && onChange({ ...data, badge: e.target.value })}
              className="bg-black/40 border border-[#00c2ff]/40 rounded px-2.5 py-0.5 text-[#00c2ff] text-xs font-bold uppercase tracking-widest focus:outline-none"
              placeholder="Badge"
            />
          ) : (
            <span className="text-[#00c2ff] text-xs font-bold uppercase tracking-widest">{data.badge}</span>
          )}
        </div>

        {/* Headline */}
        {isEditing ? (
          <div className="space-y-4 mb-8 max-w-3xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-left">
              <div>
                <label className="text-[11px] text-white/50 uppercase tracking-wider block mb-1">Prefijo del Título</label>
                <input
                  type="text"
                  value={data.titlePrefix}
                  onChange={(e) => onChange && onChange({ ...data, titlePrefix: e.target.value })}
                  className="w-full bg-black/40 border border-white/20 rounded-lg px-3 py-2 text-white font-black text-xl focus:outline-none focus:border-[#00c2ff]"
                />
              </div>
              <div>
                <label className="text-[11px] text-[#00c2ff] uppercase tracking-wider block mb-1">Título Destacado</label>
                <input
                  type="text"
                  value={data.titleHighlight}
                  onChange={(e) => onChange && onChange({ ...data, titleHighlight: e.target.value })}
                  className="w-full bg-black/40 border border-[#00c2ff]/40 rounded-lg px-3 py-2 text-cyan-300 font-black text-xl focus:outline-none focus:border-[#00c2ff]"
                />
              </div>
            </div>

            <div className="text-left">
              <label className="text-[11px] text-white/50 uppercase tracking-wider block mb-1">Subtítulo Descriptivo</label>
              <textarea
                value={data.subtitle}
                onChange={(e) => onChange && onChange({ ...data, subtitle: e.target.value })}
                rows={3}
                className="w-full bg-black/40 border border-white/15 rounded-lg px-3 py-2 text-white/80 text-sm focus:outline-none focus:border-[#00c2ff]"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-left">
              <div>
                <label className="text-[11px] text-purple-400 uppercase tracking-wider block mb-1">Botón Principal</label>
                <input
                  type="text"
                  value={data.ctaBook}
                  onChange={(e) => onChange && onChange({ ...data, ctaBook: e.target.value })}
                  className="w-full bg-black/40 border border-purple-500/40 rounded-lg px-3 py-1.5 text-white text-xs font-semibold focus:outline-none"
                />
              </div>
              <div>
                <label className="text-[11px] text-white/50 uppercase tracking-wider block mb-1">Botón Secundario</label>
                <input
                  type="text"
                  value={data.ctaTeam}
                  onChange={(e) => onChange && onChange({ ...data, ctaTeam: e.target.value })}
                  className="w-full bg-black/40 border border-white/20 rounded-lg px-3 py-1.5 text-white text-xs font-semibold focus:outline-none"
                />
              </div>
            </div>
          </div>
        ) : (
          <>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6 leading-tight">
              {data.titlePrefix}<br />
              <span className="bg-gradient-to-r from-[#00c2ff] via-[#8c52ff] to-[#FF2DAA] bg-clip-text text-transparent">
                {data.titleHighlight}
              </span>
            </h1>

            <p className="text-white/55 text-base md:text-lg leading-relaxed max-w-2xl mx-auto mb-10">
              {data.subtitle}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-14">
              <Link
                to="/contact"
                className="whitespace-nowrap inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#8c52ff] to-[#00c2ff] text-white font-bold px-8 py-4 rounded-full hover:opacity-90 transition-all duration-300 cursor-pointer"
              >
                <i className="ri-calendar-line" />
                {data.ctaBook}
              </Link>
              <Link
                to="/about"
                className="whitespace-nowrap inline-flex items-center justify-center gap-2 bg-white/5 border border-white/20 text-white font-semibold px-8 py-4 rounded-full hover:bg-white/10 transition-all duration-300 cursor-pointer"
              >
                <i className="ri-team-line" />
                {data.ctaTeam}
              </Link>
            </div>
          </>
        )}

        {/* Stats */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-8 sm:gap-14 pt-4">
          {data.stats.map((s, i) => (
            <div key={i} className="text-center">
              {isEditing ? (
                <div className="space-y-1">
                  <input
                    type="text"
                    value={s.value}
                    onChange={(e) => handleStatChange(i, 'value', e.target.value)}
                    className="w-24 bg-black/40 border border-[#00c2ff]/40 rounded px-2 py-1 text-center text-lg font-black text-cyan-300 focus:outline-none"
                  />
                  <input
                    type="text"
                    value={s.label}
                    onChange={(e) => handleStatChange(i, 'label', e.target.value)}
                    className="w-36 bg-black/40 border border-white/10 rounded px-2 py-0.5 text-center text-xs text-white/50 focus:outline-none"
                  />
                </div>
              ) : (
                <>
                  <div className="text-2xl md:text-3xl font-black bg-gradient-to-r from-[#00c2ff] to-[#8c52ff] bg-clip-text text-transparent">
                    {s.value}
                  </div>
                  <div className="text-white/40 text-xs uppercase tracking-wider mt-1">{s.label}</div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesHero;
